// Búsqueda y orden puros de las tablas POS de «Nueva venta» (Accesorio y Teléfono), sobre el
// catálogo de la tienda que ya lee el wizard.
import type { CatalogItem } from '@/domains/sales/wizard/types';
import { coincide, terminosDe } from '@/domains/sales/wizard/catalogFilters';

export type PosSortKey = 'sku' | 'brand' | 'model' | 'price' | 'stock' | 'priority';
export type SortDirection = 'asc' | 'desc';

export interface PosSort {
    key: PosSortKey;
    direction: SortDirection;
}

/** El mock arranca con «más populares primero»: la prioridad de venta del catálogo, mayor arriba. */
export const DEFAULT_POS_SORT: PosSort = { key: 'priority', direction: 'desc' };

/** Piezas en la tienda; sin dato cuenta como cero. */
export function availableOf(item: Pick<CatalogItem, 'stock'>): number {
    return item.stock ?? 0;
}

/** Nombre de la promo viva que nombra al producto; `null` si no tiene. */
export function promoLabelOf(item: Pick<CatalogItem, 'promotions'>): string | null {
    return item.promotions?.[0]?.name ?? null;
}

const byText = (a: string, b: string): number => a.localeCompare(b, 'es-MX');

/** Desempate alfabético por marca y modelo, para que el orden sea estable. */
const alphabetical = (a: CatalogItem, b: CatalogItem): number =>
    byText(a.brand, b.brand) || byText(a.model, b.model) || byText(a.sku, b.sku);

const COMPARATORS: Record<PosSortKey, (a: CatalogItem, b: CatalogItem) => number> = {
    sku: (a, b) => byText(a.sku, b.sku),
    brand: (a, b) => byText(a.brand, b.brand),
    model: (a, b) => byText(a.model, b.model),
    price: (a, b) => a.cashPriceCents - b.cashPriceCents,
    stock: (a, b) => availableOf(a) - availableOf(b),
    priority: (a, b) => (a.salePriority ?? 0) - (b.salePriority ?? 0),
};

/** Texto ascendente y cifras descendentes al estrenar columna; la activa invierte. */
export function nextPosSort(current: PosSort, key: PosSortKey): PosSort {
    if (current.key === key) {
        return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
    }
    const textual = key === 'sku' || key === 'brand' || key === 'model';
    return { key, direction: textual ? 'asc' : 'desc' };
}

/** Busca por SKU, marca o modelo (todas las palabras) y ordena. */
export function visiblePosItems(
    items: readonly CatalogItem[],
    query: string,
    sort: PosSort,
): CatalogItem[] {
    const terms = terminosDe(query);
    const sign = sort.direction === 'asc' ? 1 : -1;
    return items
        .filter((item) => coincide(item, terms))
        .sort((a, b) => COMPARATORS[sort.key](a, b) * sign || alphabetical(a, b));
}
